import { useEffect, useMemo, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../firebase';

/**
 * useActiveSignals
 * ─────────────────────────────────────────────────────────────
 * Lee /active_signals/{gid} de RTDB (snapshot inmutable que el bot
 * escribe la primera vez que un partido cumple las 4 ventanas EG1
 * del PDF — ver firebase_upload.py::_eg1_pdf_window_match).
 *
 * Separa en dos colas comparando con la línea live actual:
 *   - active   : snapshot.linea === pick.linea  →  card "Señal activa"
 *   - recorded : snapshot.linea !== pick.linea  →  registro al pie
 *                (la casa movió la línea pero el partido sigue)
 *
 * Cuando el bot detecta game_ended, borra el nodo → el partido
 * desaparece de ambas colas.
 *
 * @param {Map<string,any>|object} picksByGid - mapa gid→pick para leer
 *   linea live actual. Acepta Map o objeto plano.
 */
export function useActiveSignals(picksByGid) {
  const [snapshots, setSnapshots] = useState({});

  useEffect(() => {
    const r = ref(db, 'active_signals');
    const unsub = onValue(r, (snap) => {
      setSnapshots(snap.val() || {});
    });
    return () => unsub();
  }, []);

  return useMemo(() => {
    const active = [];
    const recorded = [];
    const getPick = (gid) => {
      if (!picksByGid) return null;
      if (typeof picksByGid.get === 'function') return picksByGid.get(gid);
      return picksByGid[gid] || null;
    };
    for (const [gid, node] of Object.entries(snapshots)) {
      if (!node) continue;
      // Multi-ventana (2026-06-12): el bot ahora escribe sub-nodos por
      // ventana /active_signals/{gid}/{Q2|Q3|Q4_m1|Q4_m6} con campo seq.
      // Formato legacy (pre-deploy): snapshot plano con partido al tope.
      // La card muestra el snapshot MÁS RECIENTE (mayor seq) del partido.
      let snap = null;
      if (node.partido) {
        snap = node; // legacy plano
      } else {
        const windows = Object.values(node).filter((s) => s && s.partido);
        if (windows.length) {
          windows.sort((a, b) => (b.seq || 0) - (a.seq || 0) ||
            (b.captured_at_ms || 0) - (a.captured_at_ms || 0));
          snap = windows[0];
        }
      }
      if (!snap) continue;
      const pick = getPick(gid);
      const currentLine = pick && typeof pick.linea === 'number' ? pick.linea : null;
      const entry = {
        id: gid,
        partido: snap.partido,
        liga: snap.liga,
        period: snap.period,
        minQ: snap.minQ,
        edge: snap.edge,
        linea: snap.linea,
        // Estado live del partido (para mostrar "ahora: Q4 · FIN" en
        // los registros y que el cliente vea dónde va el partido AHORA).
        nowPeriod: pick && pick.period != null ? pick.period : null,
        nowClock:  pick && pick.clock != null ? String(pick.clock) : null,
      };
      if (currentLine === null || Math.abs(currentLine - snap.linea) < 0.01) {
        active.push(entry);
      } else {
        recorded.push(entry);
      }
    }
    // Orden estable: capturado más reciente primero (id desc como proxy si
    // el bot no entrega timestamp adicional).
    active.sort((a, b) => a.id.localeCompare(b.id));
    recorded.sort((a, b) => a.id.localeCompare(b.id));
    return { active, recorded };
  }, [snapshots, picksByGid]);
}
